import OrgPayment from "@/atomic/organisms/orgPayment";
import Header from "@/atomic/atoms/Header";
import { PropsView } from "./Models";
import Loading from "@/atomic/atoms/Loading";
import useI18n from "@/hooks/useI18n";
import LanguageSelector from "@/atomic/atoms/LanguageSelector";
import Modal from "@/atomic/atoms/Modal";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import environment from "@/settings/environment";

export default function HomeView({
  goToVehicles,
  items,
  plate,
  isLoading,
  onChange,
  selectables,
  goBack,
  goGetout,
  onSearchNewPlate,
  isCaptchaVerified = false,
  isCaptchaMtcCaptcha = false,
  captchaName = "mtcaptcha-home-modal",
  resetCaptcha,
  incrementCaptchaKey,
}: PropsView) {
  const { t } = useI18n();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPlate, setNewPlate] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const hasInitializedCaptcha = useRef(false);

  const formattedPlate = useMemo(() => 
    plate ? plate.replace(/^([A-Za-z]{3})(\d[A-Za-z]?\d{2,3})$/, "$1-$2") : "", 
    [plate]
  );

  const isButtonDisabled = useMemo(() => 
    !newPlate || newPlate.replace(/[^A-Z0-9]/g, "").length < 5 || !isCaptchaVerified,
    [newPlate, isCaptchaVerified]
  );

  const buttonClassName = useMemo(() => 
    `w-full h-[calc(60px)] rounded-lg text-white font-medium ${
      isButtonDisabled
        ? "bg-gray-300 cursor-not-allowed"
        : "bg-black hover:bg-gray-800"
    }`,
    [isButtonDisabled]
  );

  const handlePlateInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanPlate = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setNewPlate(cleanPlate);
  }, []);

  const handleSearchClick = useCallback(() => {
    const cleanPlate = newPlate.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (cleanPlate && cleanPlate.length >= 5 && onSearchNewPlate) {
      onSearchNewPlate(cleanPlate);
      setIsModalOpen(false);
      setNewPlate("");
    }
  }, [newPlate, onSearchNewPlate]);

  const toggleModal = useCallback(() => {
    const wasOpen = isModalOpen;
    setIsModalOpen(!isModalOpen);
    
    if (wasOpen) {
      setShowCaptcha(false);
      setNewPlate("");
      
      if (isCaptchaMtcCaptcha) {
        const element = document.getElementById(captchaName);
        if (element) {
          element.remove();
        }
        if ((window as any)?.mtcaptcha?.renderQueue) {
          const queue = (window as any).mtcaptcha.renderQueue;
          const index = queue.indexOf(captchaName);
          if (index > -1) {
            queue.splice(index, 1);
          }
        }
        try {
          if ((window as any)?.mtcaptcha?.resetUI) {
            (window as any).mtcaptcha.resetUI(captchaName);
          }
        } catch (error) {
          // Ignore errors
        }
      } else {
        const recaptchaContainer = document.querySelector('.g-recaptcha');
        if (recaptchaContainer?.parentElement) {
          recaptchaContainer.parentElement.innerHTML = '';
        }
        if ((window as any).grecaptcha?.enterprise?.reset) {
          (window as any).grecaptcha.enterprise.reset();
        } else if ((window as any).grecaptcha?.reset) {
          (window as any).grecaptcha.reset();
        }
        (window as any).recaptchaToken = null;
      }
      
      resetCaptcha?.();
    } else {
      setNewPlate("");
    }
  }, [isModalOpen, isCaptchaMtcCaptcha, captchaName, resetCaptcha]);

  useEffect(() => {
    if (isModalOpen) {
      if (!hasInitializedCaptcha.current) {
        incrementCaptchaKey?.();
        hasInitializedCaptcha.current = true;
      }
      
      const timer = setTimeout(() => {
        setShowCaptcha(true);
        resetCaptcha?.();
      }, 100);
      
      return () => {
        clearTimeout(timer);
      };
    } else {
      setShowCaptcha(false);
      hasInitializedCaptcha.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  return (
    <div className="relative flex flex-1 flex-col w-full">
      <Header
        title={t("pages:home:debts")}
        hasLeftButton
        goBack={goGetout}
        rightButton={
          <LanguageSelector
            className="text-black"
            arrowImg="/arrow-bottom.png"
            showFlag={true}
          />
        }
      />
      <div className="absolute flex w-full justify-center h-[calc(280px)]">
        <img
          src={"/iStock.png"}
          width={1505}
          height={291}
          alt="iStock"
          className="absolute brightness-50 h-[calc(280px)] object-cover w-full max-w-[calc(900px)] object-top"
        />
      </div>
      <main className="z-10 mt-[calc(140px)] p-2 w-full flex justify-center h-full text-black h-form">
        <article className="h-full flex flex-col w-[calc(480px)]">
          <div className={`w-full mb-[calc(120px)] pb-[calc(120px)]`}>
            <div className="flex items-center">
              <img
                src={"/icon-car.png"}
                width={24}
                height={24}
                alt="icon car"
              />
              <p className="ml-1 text-white">{t("pages:home:yourVehicles")}</p>
            </div>
            <div className="flex gap-3 mt-1 mb-6">
              <div className="flex-1 bg-white rounded-xl border border-[#E6E6E6] p-4 flex items-center gap-3 min-h-[calc(81px)]">
                <p className="text-lg font-bold text-black uppercase">
                  {formattedPlate}
                </p>
              </div>
              <button
                className="bg-white rounded-xl border border-[#E6E6E6] w-[calc(56px)] h-[calc(81px)] flex items-center justify-center flex-shrink-0"
                type="button"
                onClick={toggleModal}
              >
                <img
                  src={"svg/icon-plus.svg"}
                  width={12}
                  height={17}
                  alt="Adicionar veículo"
                />
              </button>
            </div>

            <>
              <h2 className="text-base text-black mt-4">{t("pages:home:debts")}</h2>
              <div className="w-full h-px bg-slate-300 mt-4 mb-3" />
              {items?.length > 0 && (
                <div className="mb-4 mt-7">
                  <div className="flex items-center w-full">
                    <input
                      type="checkbox"
                      name="all"
                      className="w-6 h-6 accent-black"
                      onChange={onChange}
                      checked={items?.length === selectables?.length}
                    />
                    <p className="text-sm ml-6">
                      {t("common:select")}{" "}
                      {items?.length}{" "}
                      {items?.length !== 1 ? t("pages:home:openPassages") : t("pages:home:openPassage")}
                    </p>
                  </div>
                  <div className="w-full h-px bg-slate-300 mt-2 mb-2" />
                </div>
              )}
              {items?.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center w-full">
                    <input
                      type="checkbox"
                      name={item.id}
                      className="w-6 h-6 accent-black"
                      onChange={onChange}
                      checked={
                        selectables?.some(
                          (selectable) => selectable.name === item.id,
                        ) ?? false
                      }
                    />
                    <div className="ml-6 w-full">
                      <div className="flex w-full justify-between">
                        <p className="text-base uppercase">{plate}</p>
                        {item?.hasDate && (
                          <p className="bg-black text-white text-sm p-1 rounded-lg">
                            {item?.daysLeft > 0
                              ? `${t("common:overdue")} ${item?.daysLeft} ${item?.daysLeft > 1 ? t("common:days") : t("common:day")}`
                              : `${t("common:due")} ${item?.dtVencimento}`
                            }
                          </p>
                        )}
                      </div>
                      <div className="flex w-full justify-between">
                        <p className="text-sm blur-sm">{t("pages:home:dataUnavailable")}</p>
                        {item?.originalPrice !== item?.price && (
                          <p className="text-sm line-through">
                            R$ {item?.originalPrice}
                          </p>
                        )}
                      </div>
                      <div className="flex w-full justify-between">
                        <p className="text-base">
                          {item?.original?.nmConcessionaria}
                        </p>
                      </div>
                      <div className="flex w-full justify-between">
                        <p className="font-bold text-base flex-1 mr-2 blur-sm">
                          {t("pages:home:dataUnavailable")}
                        </p>
                        <p className="font-bold text-base whitespace-nowrap">R$ {item?.price}</p>
                      </div>
                    </div>
                  </div>
                  <div className="w-full h-px bg-slate-300 mt-2 mb-2" />
                </div>
              ))}
            </>
            {items?.length === 0 && (
              <div className="w-full flex justify-center items-center flex-col mt-10">
                <img
                  src={"/icon-car-front-close.png"}
                  width={48}
                  height={48}
                  alt="Ícone carro"
                  className="mt-6"
                />
                <h2 className="mt-1 text-sm text-center px-8">
                  {t("pages:home:passageNotFoundTitle")} <br />
                  {t("pages:home:passageNotFoundDescription")}
                </h2>
              </div>
            )}
            {items?.length === 0 && (
              <div className="border-1 border border-gray-400 w-full p-4 rounded-lg flex items-center mb-5 mt-20">
                <img
                  src={"/icon-square.png"}
                  width={40}
                  height={40}
                  alt={t("common:information")}
                  className="h-[calc(40px)] w-[calc(40px)]"
                />
                <p className="ml-2 text-gray-600 text-sm">
                  {t("pages:home:passageNotFoundTagDescription")}
                </p>
              </div>
            )}
          </div>
          {items?.length > 0 && <OrgPayment />}
        </article>
      </main>
      {isLoading && <Loading />}
      {isModalOpen && (
        <Modal
          content={
            <div className="flex flex-col">
              <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full"></div>
              <button
                type="button"
                onClick={toggleModal}
                className="absolute top-6 right-6 text-black"
              >
                <img src="/svg/icon-close.svg" alt="Fechar" className="w-6 h-6" />
              </button>
              <div className="pt-4">
                <h3 className="text-lg font-bold text-black mb-2">
                  {t("pages:home:newPlate")}
                </h3>
                <input
                  value={newPlate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const cleanPlate = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                    setNewPlate(cleanPlate);
                  }}
                  placeholder={t("pages:home:enterPlate")}
                  minLength={5}
                  maxLength={7}
                  className="w-full text-base text-black bg-transparent border border-gray-300 p-3 rounded-lg h-14 mb-4 focus:outline-none focus:border-black focus:border-2"
                  type="text"
                  autoFocus
                />
                {showCaptcha && (
                  <div className="w-full mb-4 flex justify-center">
                    {isCaptchaMtcCaptcha ? (
                      <div 
                        className="mtcaptcha" 
                        id={captchaName} 
                        data-callback="onCaptchaSuccess"
                      ></div>
                    ) : (
                      <div
                        className="g-recaptcha mx-auto"
                        data-sitekey={environment.recaptcha.siteKey}
                        data-size="normal"
                        data-theme="light"
                        data-callback="onRecaptchaVerify"
                        data-expired-callback="onRecaptchaExpired"
                        data-error-callback="onRecaptchaError"
                      />
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleSearchClick}
                  disabled={isButtonDisabled}
                  className={buttonClassName}
                >
                  {t("pages:home:searchDebts")}
                </button>
              </div>
            </div>
          }
          actionClose={toggleModal}
        />
      )}
    </div>
  );
}
